import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import GridLayout from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Plus, Settings, Maximize2, Minimize2, X, GripVertical,
  TrendingUp, Phone, Target, FileText, Users, Trophy,
  Zap, BarChart3, Activity
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import SalesKPIsWidget from '../components/director/SalesKPIsWidget';
import CallCenterKPIsWidget from '../components/director/CallCenterKPIsWidget';
import RocksWidget from '../components/director/RocksWidget';
import EstimatesWidget from '../components/director/EstimatesWidget';
import UserActivityWidget from '../components/director/UserActivityWidget';
import LeaderboardWidget from '../components/director/LeaderboardWidget';
import GoalsInitiativesWidget from '../components/director/GoalsInitiativesWidget';
import BlastModeWidget from '../components/director/BlastModeWidget';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Widget library with all available widgets
const WIDGET_LIBRARY = {
  salesKPIs: {
    id: 'salesKPIs',
    title: 'Sales Department KPIs',
    icon: TrendingUp,
    defaultSize: { w: 6, h: 4, minW: 4, minH: 3 }
  },
  callCenterKPIs: {
    id: 'callCenterKPIs',
    title: 'Call Center KPIs',
    icon: Phone,
    defaultSize: { w: 6, h: 4, minW: 4, minH: 3 }
  },
  rocks: {
    id: 'rocks',
    title: 'Rocks Tracking',
    icon: Target,
    defaultSize: { w: 6, h: 5, minW: 4, minH: 4 }
  },
  estimates: {
    id: 'estimates',
    title: 'Estimates',
    icon: FileText,
    defaultSize: { w: 6, h: 5, minW: 4, minH: 4 }
  },
  userActivity: {
    id: 'userActivity',
    title: 'User Activity',
    icon: Activity,
    defaultSize: { w: 6, h: 4, minW: 4, minH: 3 }
  },
  leaderboard: {
    id: 'leaderboard',
    title: 'Leaderboard',
    icon: Trophy,
    defaultSize: { w: 6, h: 5, minW: 4, minH: 4 }
  },
  blastMode: {
    id: 'blastMode',
    title: 'Blast Mode',
    icon: Zap,
    defaultSize: { w: 6, h: 4, minW: 4, minH: 3 }
  },
  goalsInitiatives: {
    id: 'goalsInitiatives',
    title: 'Goals & Initiatives',
    icon: BarChart3,
    defaultSize: { w: 6, h: 4, minW: 4, minH: 3 }
  }
};

const DirectorOfSalesDashboard = () => {
  const [layout, setLayout] = useState([]);
  const [widgets, setWidgets] = useState({});
  const [collapsedWidgets, setCollapsedWidgets] = useState({});
  const [showWidgetLibrary, setShowWidgetLibrary] = useState(false);

  useEffect(() => {
    loadDashboardLayout();
  }, []);

  const loadDashboardLayout = () => {
    // Load from localStorage or use default
    const savedLayout = localStorage.getItem('directorDashboardLayout');
    const savedWidgets = localStorage.getItem('directorDashboardWidgets');
    
    if (savedLayout && savedWidgets) {
      setLayout(JSON.parse(savedLayout));
      setWidgets(JSON.parse(savedWidgets));
    } else {
      // Default layout
      const defaultWidgets = {
        salesKPIs: true,
        callCenterKPIs: true,
        rocks: true,
        estimates: true,
        leaderboard: true
      };
      
      const defaultLayout = [
        { i: 'salesKPIs', x: 0, y: 0, w: 6, h: 4, minW: 4, minH: 3 },
        { i: 'callCenterKPIs', x: 6, y: 0, w: 6, h: 4, minW: 4, minH: 3 },
        { i: 'rocks', x: 0, y: 4, w: 6, h: 5, minW: 4, minH: 4 },
        { i: 'estimates', x: 6, y: 4, w: 6, h: 5, minW: 4, minH: 4 },
        { i: 'leaderboard', x: 0, y: 9, w: 12, h: 5, minW: 6, minH: 4 }
      ];
      
      setWidgets(defaultWidgets);
      setLayout(defaultLayout);
    }
  };

  const saveDashboardLayout = (newLayout, newWidgets) => {
    localStorage.setItem('directorDashboardLayout', JSON.stringify(newLayout));
    localStorage.setItem('directorDashboardWidgets', JSON.stringify(newWidgets || widgets));
  };

  const onLayoutChange = (newLayout) => {
    setLayout(newLayout);
    saveDashboardLayout(newLayout);
  };

  const addWidget = (widgetId) => {
    if (widgets[widgetId]) {
      toast.info('Widget already added');
      return;
    }

    const widgetConfig = WIDGET_LIBRARY[widgetId];
    const newWidgets = { ...widgets, [widgetId]: true };
    
    // Find next available position
    const maxY = layout.reduce((max, item) => Math.max(max, item.y + item.h), 0);
    
    const newLayout = [
      ...layout,
      {
        i: widgetId,
        x: 0,
        y: maxY,
        ...widgetConfig.defaultSize
      }
    ];
    
    setWidgets(newWidgets);
    setLayout(newLayout);
    saveDashboardLayout(newLayout, newWidgets);
    setShowWidgetLibrary(false);
    toast.success(`${widgetConfig.title} added`);
  };

  const removeWidget = (widgetId) => {
    const newWidgets = { ...widgets };
    delete newWidgets[widgetId];
    
    const newLayout = layout.filter(item => item.i !== widgetId);
    
    setWidgets(newWidgets);
    setLayout(newLayout);
    saveDashboardLayout(newLayout, newWidgets);
    toast.success('Widget removed');
  };

  const toggleCollapse = (widgetId) => {
    setCollapsedWidgets(prev => ({
      ...prev,
      [widgetId]: !prev[widgetId]
    }));
  };

  const resetLayout = () => {
    localStorage.removeItem('directorDashboardLayout');
    localStorage.removeItem('directorDashboardWidgets');
    loadDashboardLayout();
    toast.success('Layout reset to default');
  };

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Director of Sales Dashboard</h1>
            <p className="text-gray-600">Customize your command center</p>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={resetLayout}>
              <Settings className="h-4 w-4 mr-2" />
              Reset Layout
            </Button>
            <Button onClick={() => setShowWidgetLibrary(!showWidgetLibrary)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Widget
            </Button>
          </div>
        </div>

        {/* Widget Library */}
        {showWidgetLibrary && (
          <Card className="mb-6 border-blue-500 bg-blue-50 dark:bg-blue-950">
            <CardHeader>
              <CardTitle className="flex justify-between items-center">
                <span>Available Widgets</span>
                <Button variant="ghost" size="sm" onClick={() => setShowWidgetLibrary(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(WIDGET_LIBRARY).map(([key, widget]) => {
                  const Icon = widget.icon;
                  const isAdded = widgets[key];
                  
                  return (
                    <button
                      key={key}
                      onClick={() => !isAdded && addWidget(key)}
                      disabled={isAdded}
                      className={`p-4 border rounded-lg text-left transition-all ${
                        isAdded
                          ? 'bg-gray-100 dark:bg-gray-800 opacity-50 cursor-not-allowed'
                          : 'bg-white dark:bg-gray-900 hover:border-blue-500 cursor-pointer'
                      }`}
                    >
                      <Icon className="h-6 w-6 mb-2" />
                      <div className="font-semibold text-sm">{widget.title}</div>
                      {isAdded && (
                        <Badge variant="secondary" className="mt-2 text-xs">Added</Badge>
                      )}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Dashboard Grid */}
        <GridLayout
          className="layout"
          layout={layout}
          cols={12}
          rowHeight={60}
          width={1200}
          onLayoutChange={onLayoutChange}
          draggableHandle=".drag-handle"
          compactType="vertical"
          preventCollision={false}
        >
          {layout.map((item) => {
            const widgetConfig = WIDGET_LIBRARY[item.i];
            if (!widgetConfig || !widgets[item.i]) return null;
            
            const isCollapsed = collapsedWidgets[item.i];
            
            return (
              <div key={item.i} className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border">
                {/* Widget Header */}
                <div className="flex items-center justify-between p-3 border-b bg-gray-50 dark:bg-gray-900">
                  <div className="flex items-center gap-2">
                    <div className="drag-handle cursor-move">
                      <GripVertical className="h-5 w-5 text-gray-400" />
                    </div>
                    <widgetConfig.icon className="h-5 w-5" />
                    <h3 className="font-semibold">{widgetConfig.title}</h3>
                  </div>
                  
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => toggleCollapse(item.i)}
                      className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
                    >
                      {isCollapsed ? (
                        <Maximize2 className="h-4 w-4" />
                      ) : (
                        <Minimize2 className="h-4 w-4" />
                      )}
                    </button>
                    <button
                      onClick={() => removeWidget(item.i)}
                      className="p-1 hover:bg-red-100 dark:hover:bg-red-900 rounded"
                    >
                      <X className="h-4 w-4 text-red-600" />
                    </button>
                  </div>
                </div>
                
                {/* Widget Content */}
                {!isCollapsed && (
                  <div className="p-4 overflow-auto" style={{ height: 'calc(100% - 56px)' }}>
                    {renderWidgetContent(item.i)}
                  </div>
                )}
              </div>
            );
          })}
        </GridLayout>
      </div>
    </Layout>
  );
};

// Widget Content Renderers
const renderWidgetContent = (widgetId) => {
  switch (widgetId) {
    case 'salesKPIs':
      return <SalesKPIsWidget />;
    case 'callCenterKPIs':
      return <CallCenterKPIsWidget />;
    case 'rocks':
      return <RocksWidget />;
    case 'estimates':
      return <EstimatesWidget />;
    case 'userActivity':
      return <UserActivityWidget />;
    case 'leaderboard':
      return <LeaderboardWidget />;
    case 'blastMode':
      return <BlastModeWidget />;
    case 'goalsInitiatives':
      return <GoalsInitiativesWidget />;
    default:
      return <div>Widget content</div>;
  }
};

export default DirectorOfSalesDashboard;
